import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Link } from '~spa/Components/Link/Link';
import BetButton from '~spa/Components/BetButton';
import SwitchableOdds from '~spa/Components/SwitchableOdds';
import EventSchedule from '~shared/components/react/EventSchedule';
import TeamLogo from '~shared/components/react/TeamLogo';
import { getSilksBackgroundInlineStyle } from '~scenes/sport/horse-racing/utils';

import './betcard.scss';

const BetCardSimple = ({
    selectionId,
    selectionName,
    eventName,
    marketName,
    label,
    spOnly,
    active,
    tradable,
    betable,
    decimalPrice,
    fractionPrice,
    americanPrice,
    sportSlug,
    scheduledStart,
    eventUrl,
    gtmAttribute,
    categorySlug,
    teamSlug,
    extraInfo,
    tournamentLogos,
    customLinkClick,
    isInplay,
    cssClass = '',
    noBetButton = false,
    isAwayTeam = false,
    isOddsLimited = false,
}) => {

    const [ t ] = useTranslation();
    const [jockeyFilename, setJockeyFilename] = useState();
    const [jockey, setJockey] = useState();
    const [trainer, setTrainer] = useState();

    useEffect(() => {
        // extra_info comes as empty from the WS update
        // so we set the icon info and jockey/trainer info from initial load
        setJockeyFilename(extraInfo?.jockey_filename);
        setJockey(extraInfo?.jockey_name);
        setTrainer(extraInfo?.trainer_name);
    }, []);

    const selectionIcon = () => {
        if (sportSlug === 'horse-racing' && jockeyFilename) {
            const silk = getSilksBackgroundInlineStyle(jockeyFilename);
            const silkStyle = {
                backgroundImage: silk,
            };

            return <div
                className="bb-icon bb-icon--size-24x24 bb-icon__silk"
                style={silkStyle}
            ></div>;
        } else if (tournamentLogos?.showTeamLogo && categorySlug && teamSlug) {
            const shouldShowAwayJersey = Boolean(tournamentLogos?.showAwayJersey) && isAwayTeam;

            return <TeamLogo
                sportSlug={sportSlug}
                categorySlug={categorySlug}
                teamSlug={teamSlug}
                shouldShowAwayJersey={shouldShowAwayJersey}
                className="bb-icon bb-icon--size-24x24"
            />;
        }

        return <div className={`bb-icon bb-icon--size-24x24 bb-icon--${sportSlug}`} />;
    };

    const iconClass = classNames('simple-betcard__selection__icon', {
        'simple-betcard__selection__icon--inplay': isInplay,
    });

    // needed for switchable odds component
    const selection = {
        sport_slug: sportSlug,
        sp_only: spOnly,
        active,
        tradable,
        decimal_price: decimalPrice,
        fraction_price: fractionPrice,
        american_price: americanPrice,
    };

    return (
        <div className={`simple-betcard ${cssClass}`}>
            <Link
                to={eventUrl}
                gtmAttribute={`${gtmAttribute}visit-event`}
                className="simple-betcard__link"
                onClick={customLinkClick}
            >
                <div className="simple-betcard__selection">
                    <div className={iconClass}>
                        {selectionIcon()}
                        {isInplay
                            ? t('event_schedule.live')
                            : <EventSchedule startTime={scheduledStart} />
                        }
                    </div>
                    <div className="simple-betcard__selection__details">
                        <div className="simple-betcard__selection-name">
                            {selectionName}
                            {
                                noBetButton
                                ? <span className="simple-betcard__odds">
                                    <SwitchableOdds selection={selection} />
                                </span>
                                : null
                            }
                        </div>
                        <div>{marketName}</div>
                        <div>{eventName}</div>
                        {
                            jockey
                            ? <div className="simple-betcard__racing-details">
                                {`${t('racing.jockey_abbr')}: ${jockey}`}
                            </div>
                            : null
                        }
                        {
                            trainer
                            ? <div className="simple-betcard__racing-details">
                                {`${t('racing.trainer_abbr')}: ${trainer}`}
                            </div>
                            : null
                        }
                    </div>
                </div>
            </Link>
            {
                !noBetButton
                ? <BetButton
                    selection_id={selectionId}
                    label={label}
                    sp_only={spOnly}
                    betable={betable}
                    decimal_price={decimalPrice}
                    fraction_price={fractionPrice}
                    american_price={americanPrice}
                    active={active}
                    tradable={tradable}
                    sport_slug={sportSlug}
                    gtmAttribute={gtmAttribute}
                    limitOddsDisplay={isOddsLimited && decimalPrice > 999}
                />
                : null
            }
        </div>
    );
};

BetCardSimple.propTypes = {
    selectionId: PropTypes.string,
    selectionName: PropTypes.string,
    eventName: PropTypes.string,
    marketName: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    spOnly: PropTypes.bool,
    active: PropTypes.bool,
    tradable: PropTypes.bool,
    betable: PropTypes.bool,
    decimalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fractionPrice: PropTypes.string,
    americanPrice: PropTypes.string,
    sportSlug: PropTypes.string,
    scheduledStart: PropTypes.string,
    eventUrl: PropTypes.string,
    gtmAttribute: PropTypes.string,
    categorySlug: PropTypes.string,
    teamSlug: PropTypes.string,
    extraInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    tournamentLogos: PropTypes.object,
    customLinkClick: PropTypes.func,
    isInplay: PropTypes.bool,
    cssClass: PropTypes.string,
    noBetButton: PropTypes.bool,
    isAwayTeam: PropTypes.bool,
    isOddsLimited: PropTypes.bool,
};

export default BetCardSimple;
